import {
  template,
  UICorePlugin,
  type EventMap,
  type AttributeMap,
  type TemplateStringifier,
  type Level,
  type SupportedVersion
} from '@clappr/core'
import {MediaControl} from 'common/plugins/media-control'
import {Options} from 'common/types/options'
import {Events} from 'common/types/events'
import {UNKNOWN_NUMBER_ID} from 'common/constants'
import pluginHtml from './template.html'

const levelCompare = (a: Level, b: Level): number =>
  b.level.height - a.level.height

export class LevelSelector<T extends Options> extends UICorePlugin<T> {
  private levels?: Level[]
  private selectedLevelId?: number

  get name(): string {
    return 'levelSelector'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get template(): TemplateStringifier {
    return template(pluginHtml)
  }

  public get attributes(): AttributeMap {
    return {
      class: 'eplayer-skin-pane qualities'
    }
  }

  public get events(): EventMap {
    return {
      'click [data-quality-select]': 'onLevelSelect'
    }
  }

  public get levelsList(): Level[] | undefined {
    return this.levels
  }

  public get selectedLevel(): number | undefined {
    return this.selectedLevelId
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  public bindEvents(): void {
    const {activePlayback} = this.core

    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    if (this.mediaControl) {
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_RENDERED,
        this.render
      )
    }

    if (activePlayback) {
      this.listenTo(
        activePlayback,
        Events.PLAYBACK_LEVELS_AVAILABLE,
        this.fillLevels
      )
      this.listenTo(
        activePlayback,
        Events.PLAYBACK_BITRATE,
        this.updateCurrentLevel
      )
      this.listenTo(
        activePlayback,
        Events.PLAYBACK_LEVEL_SWITCH_START,
        this.onLevelChanged
      )

      if (activePlayback.levels?.length) this.fillLevels(activePlayback.levels)
    }
  }

  private onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  private onLevelChanged(): void {
    if (this.core.activePlayback.currentLevel !== this.selectedLevelId) {
      this.selectedLevelId = this.core.activePlayback.currentLevel
      this.highlightCurrentLevel()
    }
  }

  protected onLevelSelect(event: Event): void {
    const {qualitySelect = ''} = (event.target as HTMLElement).dataset
    const levelId = parseInt(qualitySelect, 10)

    this.setLevel(levelId)
  }

  public setLevel(levelId: number): void {
    if (this.core.activePlayback.currentLevel !== levelId) {
      try {
        // 54. Когда видео закончилось, падает ошибка в консоль при попытке изменить качество видео
        // (hls.js:38 Uncaught TypeError: Cannot set property 'currentLevel' of undefined)
        this.core.activePlayback.currentLevel = levelId
      } catch {}
    }
  }

  private updateCurrentLevel(info: {level: number}): void {
    const levelId = info.level

    if (this.selectedLevelId !== levelId) {
      this.highlightCurrentLevel()
    }
  }

  private highlightCurrentLevel(): void {
    const $items = this.$el.find('[data-quality-select]')

    if (this.mediaControl && $items.size()) {
      const $label = this.mediaControl.$el.find('[data-quality-label]')
      const $active = this.$el.find(
        `[data-quality-select="${this.selectedLevelId ?? UNKNOWN_NUMBER_ID}"]`
      )

      $items.removeClass('active')
      $active.addClass('active')
      $label.text(
        $active.get(0)?.textContent ?? this.core.i18n.t('quality_auto')
      )
    }
  }

  private fillLevels(levels: Level[], initialLevel = UNKNOWN_NUMBER_ID): void {
    if (this.selectedLevelId == null) this.selectedLevelId = initialLevel

    this.levels = levels
      .map((item) => ({
        ...item,
        label: `${item.level.height}p`
      }))
      .sort(levelCompare)

    this.render()
  }

  public render(): this {
    const {activePlayback, activeContainer} = this.core
    const {levels} = this

    if (
      activePlayback &&
      activeContainer &&
      activePlayback.currentLevel !== undefined &&
      levels?.length
    ) {
      this.$el.html(
        this.template({
          levels,
          quality_auto: this.core.i18n.t('quality_auto')
        })
      )

      const $container = this.mediaControl?.$el.find('[data-quality-container]')

      $container?.append(this.el)
      this.highlightCurrentLevel()
      this.trigger(Events.LEVEL_SELECTOR_RENDERED)
    }

    return this
  }
}
